import React, { useState } from 'react';
import { motion } from 'framer-motion';
import supabase from '../supabase/supabase';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const Payment = ({ user, showToast }) => {
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [showPayPal, setShowPayPal] = useState(false);
  const [payPalWindow, setPayPalWindow] = useState(null);

  const checkPayPalPayment = async (orderId) => {
    try {
      setVerifying(true);
      
      // Call your backend API to verify the PayPal order
      const response = await fetch('/api/verify-paypal', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          order_id: orderId,
          user_id: user.id
        }),
      });

      const data = await response.json();

      if (data.success && data.status === 'COMPLETED') {
        // Payment verified - create lifetime access
        await createLifetimeSubscription(data.order_id, data.amount);
        return true;
      } else {
        showToast('Payment verification failed. Please contact support.', 'error');
        return false;
      }
    } catch (error) {
      console.error('Error verifying PayPal payment:', error);
      showToast('Error verifying payment. Please contact support.', 'error');
      return false;
    } finally {
      setVerifying(false);
    }
  };

  const createLifetimeSubscription = async (orderId, amount) => {
    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .insert({
          user_id: user.id,
          plan: 'lifetime',
          status: 'active',
          amount: amount || 97.00,
          created_at: new Date().toISOString(),
          expires_at: null,
          paypal_order_id: orderId
        })
        .select()
        .single();

      if (error) throw error;

      showToast('Payment successful! You now have lifetime access to Sneaky Redirects.', 'success');
      
      // Reload the page to trigger the auth state change in App.jsx
      setTimeout(() => {
        window.location.reload();
      }, 2000);

    } catch (error) {
      console.error('Error creating subscription:', error);
      showToast('Payment was verified but there was an issue activating your access. Please contact support.', 'error');
    }
  };

  const handlePayWithPayPal = async () => {
    try {
      setLoading(true);
      
      // Create PayPal order via your backend
      const response = await fetch('/api/create-paypal-order', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: 97.00,
          currency: 'USD',
          description: 'Sneaky Redirects - Lifetime Access'
        }),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to create PayPal order');
      }

      // Open PayPal approval page
      const approvalUrl = data.approval_url;
      const windowFeatures = 'width=800,height=600,scrollbars=yes,resizable=yes';
      const newWindow = window.open(approvalUrl, '_blank', windowFeatures);
      setPayPalWindow(newWindow);

      // Poll to check if window is closed
      const checkClosed = setInterval(async () => {
        if (newWindow.closed) {
          clearInterval(checkClosed);
          setPayPalWindow(null);
          
          if (data.order_id) {
            // Check payment status when user returns
            const paymentVerified = await checkPayPalPayment(data.order_id);
            if (paymentVerified) {
              // Success handled in checkPayPalPayment
            } else {
              // Payment not verified - show error and allow retry
              showToast('Payment could not be verified. Please try again or contact support.', 'error');
            }
          }
        }
      }, 1000);

      // Cleanup after 10 minutes
      setTimeout(() => {
        clearInterval(checkClosed);
        if (newWindow && !newWindow.closed) {
          newWindow.close();
          setPayPalWindow(null);
        }
      }, 600000);

    } catch (error) {
      console.error('Error creating PayPal order:', error);
      showToast('Failed to initialize PayPal payment. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const cancelPayment = () => {
    if (payPalWindow && !payPalWindow.closed) {
      payPalWindow.close();
      setPayPalWindow(null);
    }
    setShowPayPal(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-2xl"
      >
        <div className="text-center mb-12">
          <SafeIcon icon={FiIcons.FiCreditCard} className="w-16 h-16 mx-auto mb-6 text-blue-400" />
          <h1 className="text-4xl font-bold text-white mb-4">Unlock Sneaky Redirects</h1>
          <p className="text-xl text-slate-400 mb-6">
            Get lifetime access to our URL redirection tool
          </p>
          <p className="text-sm text-slate-500">
            You're signed in as {user.email}
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="relative p-8 rounded-2xl border bg-gradient-to-br from-blue-600/20 to-purple-600/20 border-blue-500/50 backdrop-blur-sm transition-all duration-300 hover:scale-105"
        >
          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
            <span className="bg-blue-600 text-white text-xs font-semibold px-4 py-1 rounded-full">
              ONE-TIME PAYMENT
            </span>
          </div>

          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-white mb-4">Lifetime Access</h2>
            <div className="mb-6">
              <span className="text-5xl font-bold text-white">$97</span>
              <span className="text-slate-400 ml-2 text-lg">
                one-time
              </span>
            </div>
            <p className="text-slate-300 mb-8">
              Pay once and use Sneaky Redirects forever. No monthly fees, no subscriptions.
            </p>
          </div>

          <ul className="space-y-4 mb-8">
            <li className="flex items-center text-slate-300">
              <SafeIcon icon={FiIcons.FiCheck} className="w-5 h-5 text-green-400 mr-3 flex-shrink-0" />
              <span>Unlimited URL redirect generation</span>
            </li>
            <li className="flex items-center text-slate-300">
              <SafeIcon icon={FiIcons.FiCheck} className="w-5 h-5 text-green-400 mr-3 flex-shrink-0" />
              <span>AES encryption for all redirects</span>
            </li>
            <li className="flex items-center text-slate-300">
              <SafeIcon icon={FiIcons.FiCheck} className="w-5 h-5 text-green-400 mr-3 flex-shrink-0" />
              <span>Lifetime updates and new features</span>
            </li>
            <li className="flex items-center text-slate-300">
              <SafeIcon icon={FiIcons.FiCheck} className="w-5 h-5 text-green-400 mr-3 flex-shrink-0" />
              <span>Priority email support</span>
            </li>
            <li className="flex items-center text-slate-300">
              <SafeIcon icon={FiIcons.FiCheck} className="w-5 h-5 text-green-400 mr-3 flex-shrink-0" />
              <span>No recurring charges</span>
            </li>
          </ul>

          <button
            onClick={() => setShowPayPal(true)}
            disabled={loading}
            className="w-full py-4 px-6 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 disabled:text-slate-500 text-white font-semibold rounded-lg transition duration-200 flex items-center justify-center gap-3 text-lg"
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                Initializing Payment...
              </>
            ) : (
              <>
                <SafeIcon icon={FiIcons.FiCreditCard} className="w-5 h-5" />
                Pay $97 with PayPal
              </>
            )}
          </button>
        </motion.div>

        <div className="text-center mt-8">
          <p className="text-slate-500 text-sm mb-4">
            Secure payment via PayPal - instant access after payment verification
          </p>
          <div className="flex items-center justify-center gap-4 text-slate-600 text-xs">
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiIcons.FiShield} className="w-4 h-4" />
              <span>Secure Payment</span>
            </div>
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiIcons.FiZap} className="w-4 h-4" />
              <span>Instant Access</span>
            </div>
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiIcons.FiLock} className="w-4 h-4" />
              <span>Encrypted Data</span>
            </div>
          </div>
        </div>

        {/* PayPal Modal */}
        {showPayPal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50"
            onClick={cancelPayment}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-slate-800 rounded-2xl p-8 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-white mb-2">Complete Payment</h3>
                <p className="text-slate-400">
                  Lifetime Access - $97.00 (one-time payment)
                </p>
              </div>

              <div className="space-y-4">
                <button
                  onClick={handlePayWithPayPal}
                  disabled={loading || verifying}
                  className="w-full py-4 px-6 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 disabled:text-slate-500 text-white font-semibold rounded-lg transition duration-200 flex items-center justify-center gap-3"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Initializing Payment...
                    </>
                  ) : verifying ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Verifying Payment...
                    </>
                  ) : (
                    <>
                      <SafeIcon icon={FiIcons.FiCreditCard} className="w-5 h-5" />
                      Pay $97 with PayPal
                    </>
                  )}
                </button>

                <div className="text-center text-slate-500 text-sm">
                  <p>You'll be redirected to PayPal to complete your payment.</p>
                  <p>After payment, please return to this page to verify and activate your lifetime access.</p>
                </div>
              </div>

              <button
                onClick={cancelPayment}
                disabled={loading || verifying}
                className="w-full mt-6 py-2 px-4 bg-slate-700 hover:bg-slate-600 disabled:bg-slate-600 disabled:text-slate-500 text-white rounded-lg transition duration-200"
              >
                Cancel
              </button>
            </motion.div>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default Payment;